import React, { useState, useEffect } from 'react';
import { InputMask } from '@react-input/mask';
import Select from "react-select";
import CurrencyInput from "react-currency-input-field";
import DatePicker, { dateHelpers } from '../../../../../components/utils/datepicker';
import api from '../../../../../config/axios';
import Swal from 'sweetalert2';

import { autoCompleteBancos, autoCompleteGestoras, autoCompleteUnidades } from "../../../../../components/utils/autocompletes";
import { FaRegSave, FaTimes } from 'react-icons/fa';

const ContaModal = ({ conta, onClose, onContaAtualizada }) => {
    const { inputPesquisaBanco, bancosOptions, handleAutoCompleteBancos, handleFocusBancos } = autoCompleteBancos();
    const { inputPesquisaGestora, gestorasOptions, handleAutoCompleteGestoras, handleFocusGestoras } = autoCompleteGestoras();
    const { inputPesquisaUnidade, unidadesOptions, handleAutoCompleteUnidades, handleFocusUnidades } = autoCompleteUnidades();
    const [formData, setFormData] = useState({
        banco_id: '',
        gestora_id: '',
        unidade_id: '',
        nome_exibicao: '',
        agencia: '',
        numero: '',
        tipo_conta: 'corrente',
        saldo_inicial: '',
        data_saldo_inicial: '',
        faz_conciliacao: false,
        usa_pix: false,
        pix_accountid: '',
        pix_auth_email: '',
        pix_auth_pwd: '',
        pix_auth_role: '',
        pix_qrcode_site: '',
        pix_qrcode_type: '',
        pix_qrcode_size: '',
        pix_qrcode_duration: '',
        pix_qrcode_path_qr: '',
        pix_qrcode_path_pix: '',
        pix_webhook_path: '',
        pix_searchbyid_path: '',
        ativo: true,
        considera_balanco: true
    });

    const [tituloModal, setTituloModal] = useState("");
    const [saldoAtual, setSaldoAtual] = useState(null);

    const tiposConta = [
        { value: 'corrente', label: 'Corrente' },
        { value: 'poupanca', label: 'Poupança' },
        { value: 'investimento', label: 'Investimento' }
    ];

    useEffect(() => {
        if (conta != null) {
            const bancoSelect = conta.banco && conta.banco.id && conta.banco.nome ? {
                value: conta.banco.id,
                label: conta.banco.codigo ? `${conta.banco.codigo} - ${conta.banco.nome}` : conta.banco.nome
            } : '';

            const gestoraSelect = conta.gestora && conta.gestora.id && conta.gestora.nome ? {
                value: conta.gestora.id,
                label: conta.gestora.nome
            } : '';

            const unidadeSelect = conta.unidade && conta.unidade.id && conta.unidade.nome ? {
                value: conta.unidade.id,
                label: conta.unidade.nome
            } : '';
            
            setFormData({
                banco_id: bancoSelect,
                gestora_id: gestoraSelect,
                unidade_id: unidadeSelect,
                nome_exibicao: conta.nome_exibicao || '',
                agencia: conta.agencia || '',
                numero: conta.numero || '',
                tipo_conta: conta.tipo_conta || 'corrente',
                saldo_inicial: conta.saldo_inicial || '',
                data_saldo_inicial: conta.data_saldo_inicial ?
                    (conta.data_saldo_inicial.includes('-') ?
                        (() => {
                            const [ano, mes, dia] = conta.data_saldo_inicial.split('-');
                            return `${dia.padStart(2, '0')}/${mes.padStart(2, '0')}/${ano}`;
                        })() :
                        conta.data_saldo_inicial) : '',
                faz_conciliacao: conta.faz_conciliacao || false,
                usa_pix: conta.usa_pix || false,
                pix_accountid: conta.pix_accountid || '',
                pix_auth_email: conta.pix_auth_email || '',
                pix_auth_pwd: conta.pix_auth_pwd || '',
                pix_auth_role: conta.pix_auth_role || '',
                pix_qrcode_site: conta.pix_qrcode_site || '',
                pix_qrcode_type: conta.pix_qrcode_type || '',
                pix_qrcode_size: conta.pix_qrcode_size || '',
                pix_qrcode_duration: conta.pix_qrcode_duration || '',
                pix_qrcode_path_qr: conta.pix_qrcode_path_qr || '',
                pix_qrcode_path_pix: conta.pix_qrcode_path_pix || '',
                pix_webhook_path: conta.pix_webhook_path || '',
                pix_searchbyid_path: conta.pix_searchbyid_path || '',
                ativo: conta.ativo !== undefined ? conta.ativo : true,
                considera_balanco: conta.considera_balanco !== undefined ? conta.considera_balanco : true
            });
            setTituloModal('Editar Conta Bancária');
            fetchSaldoAtual(conta.id);
        } else {
            setTituloModal('Nova Conta Bancária');
            setSaldoAtual(null);
        }
    }, [conta]);

    const fetchSaldoAtual = async (contaId) => {
        try {
            const response = await api.get(`/contas-bancarias/${contaId}/saldo`);
            setSaldoAtual(response.data.saldo_atual);
        } catch (error) {
            setSaldoAtual(null);
        }
    };

    const formatarValor = (valor) => {
        if (valor === null || valor === undefined) return '-';
        return parseFloat(valor).toLocaleString('pt-BR', {
            style: 'currency',
            currency: 'BRL'
        });
    };

    useEffect(() => {
        if (!formData.gestora_id && formData.unidade_id) {
            setFormData(prev => ({
                ...prev,
                unidade_id: ''
            }));
        }
    }, [formData.gestora_id]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = value;
        
        if (name === 'ativo') {
            finalValue = value === 'true';
        } else if (type === 'checkbox') {
            finalValue = checked;
        }
        
        setFormData(prev => ({
            ...prev,
            [name]: finalValue
        }));
    };

    const handleSelectChange = (name, selectedOption) => {
        setFormData(prev => ({
            ...prev,
            [name]: selectedOption
        }));

        if (name === 'gestora_id') {
            setFormData(prev => ({
                ...prev,
                [name]: selectedOption,
                unidade_id: ''
            }));
        }
    };

    const handleDateChange = (field, formattedDate) => {
        setFormData(prev => ({
            ...prev,
            [field]: formattedDate
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!formData.banco_id || !formData.banco_id.value) {
            Swal.fire('Atenção', 'O banco é obrigatório.', 'warning');
            return;
        }

        if (!formData.gestora_id || !formData.gestora_id.value) {
            Swal.fire('Atenção', 'A gestora é obrigatória.', 'warning');
            return;
        }

        if (!formData.nome_exibicao.trim()) {
            Swal.fire('Atenção', 'O nome de exibição é obrigatório.', 'warning');
            return;
        }

        if (!formData.tipo_conta) {
            Swal.fire('Atenção', 'O tipo da conta é obrigatório.', 'warning');
            return;
        }

        Swal.fire({
            title: "Carregando...",
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        try {
            const dados = { ...formData };
            
            if (dados.banco_id && dados.banco_id.value) {
                dados.banco_id = dados.banco_id.value;
            }
            
            if (dados.gestora_id && dados.gestora_id.value) {
                dados.gestora_id = dados.gestora_id.value;
            }

            if (dados.unidade_id && dados.unidade_id.value) {
                dados.unidade_id = dados.unidade_id.value;
            }

            if (dados.saldo_inicial && typeof dados.saldo_inicial === 'string') {
                dados.saldo_inicial = parseFloat(dados.saldo_inicial.replace(',', '.')) || 0;
            }

            if (dados.data_saldo_inicial && dados.data_saldo_inicial.includes('/')) {
                const [dia, mes, ano] = dados.data_saldo_inicial.split('/');
                dados.data_saldo_inicial = `${ano}-${mes.padStart(2, '0')}-${dia.padStart(2, '0')}`;
            }
            
            Object.keys(dados).forEach(key => {
                if (dados[key] === '') {
                    dados[key] = null;
                }
            });

            if (conta) {
                await api.put(`/contas-bancarias/${conta.id}`, dados);
                Swal.fire("Sucesso", "Conta atualizada com sucesso!", "success");
            } else {
                await api.post('/contas-bancarias', dados);
                Swal.fire("Sucesso", "Conta criada com sucesso!", "success");
            }

            onContaAtualizada();
        } catch (error) {
            Swal.close();
            Swal.fire('Erro', error.response.data.message || 'Erro ao salvar conta.', 'error');
        }
    };

    return (
        <div className="modal fade show" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
            <div className="modal-dialog modal-xl modal-dialog-scrollable modal-dialog-centered" style={{ maxHeight: '90vh' }}>
                <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxHeight: '90vh' }}>
                    <div className="modal-header">
                        <h4 className="fw-bold mt-2">{tituloModal}</h4>
                        <button type="button" className="btn-close" onClick={onClose} aria-label="Close"></button>
                    </div>

                    <form onSubmit={handleSubmit}>
                        <div className="modal-body" style={{ maxHeight: 'calc(90vh - 180px)', overflowY: 'auto' }}>
                            <div className="row">
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Banco</label>
                                    <Select
                                        options={bancosOptions}
                                        onInputChange={(value) => {
                                            handleAutoCompleteBancos(value);
                                        }}
                                        onFocus={handleFocusBancos}
                                        onChange={(value) => handleSelectChange('banco_id', value)}
                                        placeholder="Digite o nome do banco"
                                        isClearable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        noOptionsMessage={() => "Sem opções"}
                                        value={formData.banco_id}
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Gestora</label>
                                    <Select
                                        options={gestorasOptions}
                                        onInputChange={(value) => {
                                            handleAutoCompleteGestoras(value);
                                        }}
                                        onFocus={handleFocusGestoras}
                                        onChange={(value) => handleSelectChange('gestora_id', value)}
                                        placeholder="Digite o nome da gestora"
                                        isClearable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        noOptionsMessage={() => "Sem opções"}
                                        value={formData.gestora_id}
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Unidade</label>
                                    <Select
                                        options={unidadesOptions}
                                        onInputChange={(value) => {
                                            if (formData.gestora_id?.value) {
                                                handleAutoCompleteUnidades(value, formData.gestora_id.value);
                                            }
                                        }}
                                        onFocus={() => handleFocusUnidades(formData.gestora_id?.value)}
                                        onChange={(value) => handleSelectChange('unidade_id', value)}
                                        placeholder="Digite o nome da unidade"
                                        isClearable
                                        menuPlacement="auto"
                                        menuPosition="fixed"
                                        noOptionsMessage={() =>
                                            !formData.gestora_id?.value
                                                ? "Selecione uma gestora primeiro"
                                                : "Sem opções"
                                        }
                                        value={formData.unidade_id}
                                        isDisabled={!formData.gestora_id?.value}
                                    />
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-lg-8 col-12">
                                    <label className="form-label fw-bold">Nome de Exibição</label>
                                    <input
                                        type="text"
                                        name="nome_exibicao"
                                        className="form-control"
                                        value={formData.nome_exibicao}
                                        onChange={handleChange}
                                        required
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Tipo da Conta</label>
                                    <select
                                        name="tipo_conta"
                                        className="form-control"
                                        value={formData.tipo_conta}
                                        onChange={handleChange}
                                        required
                                    >
                                        {tiposConta.map(tipo => (
                                            <option key={tipo.value} value={tipo.value}>
                                                {tipo.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Agência</label>
                                    <input
                                        type="text"
                                        name="agencia"
                                        className="form-control"
                                        value={formData.agencia}
                                        onChange={handleChange}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Número da Conta</label>
                                    <input
                                        type="text"
                                        name="numero"
                                        className="form-control"
                                        value={formData.numero}
                                        onChange={handleChange}
                                        autoComplete="off"
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Status</label>
                                    <select
                                        name="ativo"
                                        className="form-control"
                                        value={formData.ativo}
                                        onChange={handleChange}
                                    >
                                        <option value={true}>Ativo</option>
                                        <option value={false}>Inativo</option>
                                    </select>
                                </div>
                            </div>

                            <div className="row mt-3">
                                <div className="col-lg-4 col-12">
                                    <label className="form-label fw-bold">Valor do Saldo Inicial</label>
                                    <CurrencyInput
                                        name="saldo_inicial"
                                        placeholder="R$ 0,00"
                                        value={formData.saldo_inicial}
                                        decimalsLimit={2}
                                        decimalSeparator=","
                                        groupSeparator="."
                                        prefix="R$ "
                                        className="form-control"
                                        autoComplete="off"
                                        onValueChange={(value) =>
                                            setFormData((prevState) => ({
                                                ...prevState,
                                                saldo_inicial: value,
                                            }))
                                        }
                                    />
                                </div>
                                <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                    <label className="form-label fw-bold">Data do Saldo Inicial</label>
                                    <DatePicker
                                        selected={dateHelpers.stringToDate(formData.data_saldo_inicial)}
                                        onChange={dateHelpers.createOnChangeHandler((formattedDate) =>
                                            handleDateChange('data_saldo_inicial', formattedDate)
                                        )}
                                        className="form-control"
                                        popperProps={{
                                            strategy: "fixed"
                                        }}
                                    />
                                </div>
                                {conta && (
                                    <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                        <label className="form-label fw-bold">Saldo Atual</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            value={formatarValor(saldoAtual)}
                                            disabled
                                        />
                                    </div>
                                )}
                            </div>

                            <div className="row mt-3">
                                <div className="col-lg-4 col-12">
                                    <div className="form-check mt-2">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="faz_conciliacao"
                                            checked={formData.faz_conciliacao}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label fw-bold">
                                            Faz Conciliação Bancária
                                        </label>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-12">
                                    <div className="form-check mt-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="usa_pix"
                                            checked={formData.usa_pix}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label fw-bold">
                                            Usa PIX
                                        </label>
                                    </div>
                                </div>
                                <div className="col-lg-4 col-12">
                                    <div className="form-check mt-3">
                                        <input
                                            className="form-check-input"
                                            type="checkbox"
                                            name="considera_balanco"
                                            checked={formData.considera_balanco}
                                            onChange={handleChange}
                                        />
                                        <label className="form-check-label fw-bold">
                                            Considerar conta no balanço patrimonial
                                        </label>
                                    </div>
                                </div>
                            </div>

                            {formData.usa_pix && (
                                <>
                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            <label className="form-label fw-bold">PIX Account ID</label>
                                            <input
                                                type="text"
                                                name="pix_accountid"
                                                className="form-control"
                                                value={formData.pix_accountid}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="col-lg-6 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">PIX Auth Email</label>
                                            <input
                                                type="email"
                                                name="pix_auth_email"
                                                className="form-control"
                                                value={formData.pix_auth_email}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            <label className="form-label fw-bold">PIX Auth Password</label>
                                            <input
                                                type="password"
                                                name="pix_auth_pwd"
                                                className="form-control"
                                                value={formData.pix_auth_pwd}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="col-lg-6 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">PIX Auth Role</label>
                                            <input
                                                type="text"
                                                name="pix_auth_role"
                                                className="form-control"
                                                value={formData.pix_auth_role}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            <label className="form-label fw-bold">QR Code Site</label>
                                            <input
                                                type="url"
                                                name="pix_qrcode_site"
                                                className="form-control"
                                                value={formData.pix_qrcode_site}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="col-lg-6 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">QR Code Type</label>
                                            <input
                                                type="text"
                                                name="pix_qrcode_type"
                                                className="form-control"
                                                value={formData.pix_qrcode_type}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-3">
                                        <div className="col-lg-4 col-12">
                                            <label className="form-label fw-bold">QR Code Size</label>
                                            <input
                                                type="text"
                                                name="pix_qrcode_size"
                                                className="form-control"
                                                value={formData.pix_qrcode_size}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">QR Code Duration</label>
                                            <input
                                                type="text"
                                                name="pix_qrcode_duration"
                                                className="form-control"
                                                value={formData.pix_qrcode_duration}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="col-lg-4 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">Webhook Path</label>
                                            <input
                                                type="text"
                                                name="pix_webhook_path"
                                                className="form-control"
                                                value={formData.pix_webhook_path}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            <label className="form-label fw-bold">QR Code Path QR</label>
                                            <input
                                                type="text"
                                                name="pix_qrcode_path_qr"
                                                className="form-control"
                                                value={formData.pix_qrcode_path_qr}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                        <div className="col-lg-6 col-12 mt-lg-0 mt-3">
                                            <label className="form-label fw-bold">QR Code Path PIX</label>
                                            <input
                                                type="text"
                                                name="pix_qrcode_path_pix"
                                                className="form-control"
                                                value={formData.pix_qrcode_path_pix}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>

                                    <div className="row mt-3">
                                        <div className="col-lg-6 col-12">
                                            <label className="form-label fw-bold">Search by ID Path</label>
                                            <input
                                                type="text"
                                                name="pix_searchbyid_path"
                                                className="form-control"
                                                value={formData.pix_searchbyid_path}
                                                onChange={handleChange}
                                                autoComplete="off"
                                            />
                                        </div>
                                    </div>
                                </>
                            )}
                        </div>

                        <div className="modal-footer mt-4">
                            <div className="row">
                                <div className="col-12 text-end">
                                    <button className="btn btn-outline-danger" type="button" onClick={onClose}>
                                        <FaTimes /> Fechar
                                    </button>
                                    <button className="btn btn-custom-primary ms-3" type="submit">
                                        <FaRegSave /> Salvar
                                    </button>
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default ContaModal; 